import { z } from 'zod';

/** A v4/v7 UUID as used for every entity id. */
export const Uuid = z.uuid();
export type Uuid = z.infer<typeof Uuid>;

/** RFC3339 / ISO-8601 timestamp with timezone, e.g. 2026-06-12T13:05:00Z. */
export const IsoDateTime = z.iso.datetime({ offset: true });
export type IsoDateTime = z.infer<typeof IsoDateTime>;

/** Calendar date with no time component, e.g. 2026-06-12. */
export const IsoDate = z.iso.date();
export type IsoDate = z.infer<typeof IsoDate>;

/** Wall-clock time of day, minute precision, e.g. "09:30". */
export const TimeOfDay = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM 24-hour time');
export type TimeOfDay = z.infer<typeof TimeOfDay>;

/**
 * Money is always an integer number of **pence** (GBP minor units). We never use floats
 * for money. A £140.00 service is 14000. Bounded by the PostgreSQL `integer` column max
 * (2,147,483,647 ≈ £21.4M) so an out-of-range amount is rejected at validation — never charged at
 * Stripe and then lost to an integer-overflow INSERT failure.
 */
export const MAX_PENCE = 2_147_483_647;
export const Pence = z.int().min(0).max(MAX_PENCE).describe('GBP amount in integer pence');
export type Pence = z.infer<typeof Pence>;

/** Signed pence (e.g. card fees, discounts). */
export const SignedPence = z
  .int()
  .min(-MAX_PENCE)
  .max(MAX_PENCE)
  .describe('GBP amount in integer pence (may be negative)');

/** E.164 phone number, e.g. +447700900123. */
export const Phone = z
  .string()
  .regex(/^\+[1-9]\d{6,14}$/, 'expected E.164 phone number, e.g. +447700900123');
export type Phone = z.infer<typeof Phone>;

export const Email = z.email().max(254);
export type Email = z.infer<typeof Email>;

/** URL-safe slug used in public paths such as salonomy.com/q/<slug>. */
export const Slug = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'lowercase letters, digits and hyphens only');
export type Slug = z.infer<typeof Slug>;

export const NonEmptyName = z.string().trim().min(1).max(120);

/** Standard cursor/offset pagination query. */
export const Pagination = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type Pagination = z.infer<typeof Pagination>;

export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    pagination: z.object({
      limit: z.int(),
      offset: z.int(),
      total: z.int(),
    }),
  });
}

/** Uniform API error envelope returned for every non-2xx response. */
export const ApiError = z
  .object({
    error: z.object({
      code: z.string(),
      message: z.string(),
      details: z.unknown().optional(),
      requestId: z.string().optional(),
    }),
  })
  .meta({ id: 'ApiError' });
export type ApiError = z.infer<typeof ApiError>;
